import React from "react";
import { useNavigate } from "react-router-dom";
import { ScrollText, UserCheck, Store, Gavel, HandHeart, CreditCard, Truck, RotateCcw, Ban, ShieldAlert, RefreshCw, Mail } from "lucide-react";
import MarketingLayout from "@/components/MarketingLayout";

function Section({ icon: Icon, title, children }) {
  return (
    <div className="ss-card" style={{ padding: 24, marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <span style={{ width: 38, height: 38, borderRadius: 10, background: "var(--brand)", color: "#fff", display: "grid", placeItems: "center", flexShrink: 0 }}>
          <Icon size={19} />
        </span>
        <h2 style={{ fontSize: 20, fontWeight: 800 }}>{title}</h2>
      </div>
      <div style={{ color: "var(--muted)", fontSize: 15, lineHeight: 1.7 }}>{children}</div>
    </div>
  );
}

export default function Terms() {
  const navigate = useNavigate();
  const updated = "June 2026";
  return (
    <MarketingLayout>
      <div className="fade-up" data-testid="terms-page">
        {/* Hero */}
        <div className="ss-card" style={{ padding: 34, background: "var(--brand)", color: "#fff" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,.14)", borderRadius: 999, padding: "6px 14px", fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>
            <ScrollText size={14} style={{ color: "var(--gold)" }} /> The rules of the marketplace
          </div>
          <h1 style={{ fontSize: 34, fontWeight: 800, marginTop: 16, lineHeight: 1.2 }}>Terms of Service</h1>
          <p style={{ marginTop: 14, fontSize: 16, lineHeight: 1.7, color: "rgba(255,255,255,.9)", maxWidth: 640 }}>
            These terms set out the rules for using SouqStream as a buyer or seller. By creating an account, joining a
            live show, buying, selling, bidding, or donating, you agree to these Terms of Service.
          </p>
          <div style={{ marginTop: 14, fontSize: 13, color: "rgba(255,255,255,.8)" }}>Last updated: {updated}</div>
        </div>

        <Section icon={UserCheck} title="Your account">
          <p>
            SouqStream is invite-only. You must provide accurate information, keep your login secure, and be
            responsible for activity on your account. You must be at least 18 (or the age of majority in your region)
            to buy or sell. We may suspend or close accounts that break these terms or our community standards.
          </p>
        </Section>

        <Section icon={Store} title="Selling on SouqStream">
          <p>Sellers are responsible for the items they list and sell. When you sell, you agree to:</p>
          <ul style={{ margin: "10px 0 0", paddingLeft: 20 }}>
            <li>List items you own and are legally allowed to sell, with honest descriptions, photos, and pricing.</li>
            <li>Honor sales, auction outcomes, and Buy-Now purchases made during your shows.</li>
            <li>Ship orders promptly with valid tracking, and handle returns fairly within the return window.</li>
            <li>Not sell prohibited, counterfeit, unsafe, or otherwise unlawful goods.</li>
          </ul>
        </Section>

        <Section icon={Gavel} title="Buying, bidding & auctions">
          <p>
            Bids and Buy-Now purchases are binding commitments to pay. If you win an auction or complete a Buy-Now,
            you agree to pay the full amount plus any applicable shipping and taxes. Prices and availability are set by
            sellers and shown in real time during live shows.
          </p>
        </Section>

        <Section icon={HandHeart} title="Fundraisers & donations">
          <p>
            Donations made through Fundraiser shows are voluntary contributions and are generally non-refundable.
            SouqStream is a platform for hosting these campaigns; the seller/organizer is responsible for how funds are
            used and for any promised perks. Donations may not be tax-deductible unless the organizer states otherwise.
          </p>
        </Section>

        <Section icon={CreditCard} title="Payments, fees & payouts">
          <p>
            Payments and seller payouts are processed by <b>Stripe</b>. SouqStream charges a platform fee on each sale
            (shown in your seller finances). Sellers connect a Stripe account to receive payouts directly to their bank;
            payouts are subject to Stripe verification and payout schedules. You authorize us and Stripe to process the
            relevant charges, transfers, and refunds.
          </p>
        </Section>

        <Section icon={Truck} title="Shipping">
          <p>
            Shipping costs, labels, and tracking are handled through the platform's shipping tools. A small platform
            markup may apply to shipping. Buyers are responsible for providing a correct shipping address; sellers are
            responsible for packaging items safely and shipping on time.
          </p>
        </Section>

        <Section icon={RotateCcw} title="Returns & refunds">
          <p>
            Eligible orders may be returned within the stated return window (typically 14 days after delivery). Approved
            returns and refunds are issued through Stripe to your original payment method. Some items, and voluntary
            donations, may not be eligible for return or refund.
          </p>
        </Section>

        <Section icon={Ban} title="Prohibited conduct">
          <p>You agree not to:</p>
          <ul style={{ margin: "10px 0 0", paddingLeft: 20 }}>
            <li>Harass, threaten, or abuse other users, or post hateful, illegal, or explicit content.</li>
            <li>Circumvent SouqStream to avoid fees, or attempt fraud, chargebacks, or fake bids.</li>
            <li>Interfere with the platform, its security, or other users' experience.</li>
          </ul>
          <p style={{ marginTop: 10 }}>Violations may result in removal of content, suspension, or a permanent ban.</p>
        </Section>

        <Section icon={ShieldAlert} title="Disclaimers & liability">
          <p>
            SouqStream is provided "as is." We are a marketplace that connects buyers and sellers and are not the seller
            of items listed by third parties. To the fullest extent permitted by law, SouqStream is not liable for
            indirect or consequential damages, and our total liability is limited to the fees you paid us for the
            relevant transaction.
          </p>
        </Section>

        <Section icon={RefreshCw} title="Changes to these terms">
          <p>
            We may update these Terms from time to time. When we do, we will revise the "Last updated" date above.
            Continued use of SouqStream after changes means you accept the updated Terms. See also our{" "}
            <span onClick={() => navigate("/privacy")} style={{ color: "var(--brand)", fontWeight: 700, cursor: "pointer" }} data-testid="terms-privacy-link">Privacy Policy</span>.
          </p>
        </Section>

        <Section icon={Mail} title="Contact us">
          <p>
            Questions about these Terms? Reach us at{" "}
            <a href="mailto:souqstream@gmail.com" style={{ color: "var(--brand)", fontWeight: 700 }}>souqstream@gmail.com</a>{" "}
            or through our{" "}
            <span onClick={() => navigate("/contact")} style={{ color: "var(--brand)", fontWeight: 700, cursor: "pointer" }} data-testid="terms-contact-link">Contact page</span>.
          </p>
        </Section>
      </div>
    </MarketingLayout>
  );
}
